import { getLocalizedNumber } from 'src/Libs/languages/functions/default'

type SIPrefix = { scale: number; symbol: string; limit: number }
type SIPrefixTable = SIPrefix[]

const SI_PREFIXES: SIPrefixTable = [
  { scale: 1e9, symbol: 'G', limit: 1e10 },
  { scale: 1e6, symbol: 'M', limit: 1e7 },
  { scale: 1e3, symbol: 'k', limit: 1e3 },
  { scale: 1, symbol: '', limit: 0 },
]

/**
 * Statistics charts use a higher kW→MW boundary than live values: values stay in kW up to 99,999 kW
 * (just under 1e8 W) and only switch to MW from 100,000 kW (1e8 W) onwards. Live values keep switching
 * to MW from 10,000 kW (1e7 W) - see `SI_PREFIXES`.
 */
export const STATISTICS_SI_PREFIXES: SIPrefixTable = [
  { scale: 1e9, symbol: 'G', limit: 1e10 },
  { scale: 1e6, symbol: 'M', limit: 1e8 },
  { scale: 1e3, symbol: 'k', limit: 1e3 },
  { scale: 1, symbol: '', limit: 0 },
]

export const NO_PREFIX_INDEX = 3

const getRoundedNumber = (absoluteValue: number, p: SIPrefix): { value: number; precision: number } => {
  const scaledValue = absoluteValue / p.scale

  if (p.scale === 1) {
    // no decimals for watt
    return { value: Math.round(scaledValue), precision: 0 }
  }

  if (scaledValue < 100) {
    // two decimals if value is < 100
    return { value: Math.round(scaledValue * 100) / 100, precision: 2 }
  }

  if (scaledValue >= 100 && scaledValue < 1000) {
    // one decimal if value is > 100 and < 1000
    return { value: Math.round(scaledValue * 10) / 10, precision: 1 }
  }

  // no decimal otherwise
  return { value: Math.round(scaledValue), precision: 0 }
}

export const formatNumberWithSIPrefix = (
  value: number,
  minPrefix = 0,
  exactPrefix?: number,
  prefixes: SIPrefixTable = SI_PREFIXES
): [number, string, number, number] => {
  if (Number.isNaN(value)) {
    return [0, '', 0, 0]
  }

  const sign = Math.sign(value)
  const absoluteValue = Math.abs(value)

  if (exactPrefix !== undefined) {
    const prefixIndex = NO_PREFIX_INDEX - exactPrefix
    const prefix = prefixes[prefixIndex]
    if (!prefix) {
      return [0, '', 0, 0]
    }

    const { value: roundedInPrefix, precision } = getRoundedNumber(absoluteValue, prefix)
    return [roundedInPrefix * sign, prefix.symbol, exactPrefix, precision]
  }

  for (let i = 0; i < prefixes.length; i++) {
    const p = prefixes[i]

    if (absoluteValue >= p.limit || NO_PREFIX_INDEX - minPrefix <= i) {
      const { value: rounded, precision } = getRoundedNumber(absoluteValue, p)

      // handle overflow of rounded values to the next prefix tier (e.g. 999_999 -> 999.999 -> 1000 (k) → 1M)
      if (i > 0 && rounded >= prefixes[i - 1].limit / p.scale) {
        return formatNumberWithSIPrefix(rounded * p.scale, minPrefix, undefined, prefixes)
      }
      return [rounded * sign, p.symbol, NO_PREFIX_INDEX - i, precision]
    }
  }
  return [value, '', NO_PREFIX_INDEX, 0]
}

export const getLocalizedValueWithPrefix = (
  value: number,
  minPrefix = 0,
  exactPrefix?: number,
  prefixes: SIPrefixTable = SI_PREFIXES
): { localizedValue: string; prefix: string; prefixNumber: number } => {
  const [val, prefix, prefixNumber, precision] = formatNumberWithSIPrefix(value, minPrefix, exactPrefix, prefixes)
  const localizedValue = getLocalizedNumber(val, precision)

  return {
    localizedValue,
    prefix,
    prefixNumber,
  }
}

export const stringWithSIPrefix = (
  value: number,
  unit: string,
  minPrefix = 0,
  exactPrefix?: number,
  prefixes: SIPrefixTable = SI_PREFIXES
): string => {
  const { localizedValue, prefix } = getLocalizedValueWithPrefix(value, minPrefix, exactPrefix, prefixes)

  return `${localizedValue} ${prefix}${unit}`
}
